import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LiquidPageHeader } from "@/components/liquid/LiquidPageHeader";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { pageVideo } from "@/lib/videos";
import { EnquiryForm } from "@/components/EnquiryForm";
import { FAQS } from "@/lib/content";
import { useContent } from "@/context/ContentContext";

export default function ContactLiquid() {
  const { content } = useContent();
  const s = content?.settings || {};
  const [open, setOpen] = useState(0);

  const rows = [
    { icon: Phone, label: "Call Us", value: s.phone || "+971 4 276 9797", href: `tel:${(s.phone || "").replace(/[^+\d]/g, "")}`, testid: "contact-call-card" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat instantly with our concierge", href: s.whatsapp ? `https://wa.me/${s.whatsapp}` : null, testid: "contact-whatsapp-card" },
    { icon: Mail, label: "Email", value: s.email || "info@royalskin.clinic", href: `mailto:${s.email || "info@royalskin.clinic"}`, testid: "contact-email-card" },
    { icon: MapPin, label: "Visit Us", value: s.address || "Dubai Healthcare City", href: null, testid: "contact-address-card" },
    { icon: Clock, label: "Opening Hours", value: s.hours || "Mon – Sat: 12:00 PM – 8:00 PM", href: null, testid: "contact-hours-card" },
  ];

  return (
    <div className="relative z-[1] px-4 pb-24 pt-36 sm:px-8 sm:pt-44 lg:px-16" data-testid="contact-liquid">
      <VideoBackdrop src={pageVideo(s, "contact")} testid="contact-video-bg" />
      <div className="mx-auto max-w-6xl">
        <LiquidPageHeader
          index="06"
          eyebrow="Contact & Booking"
          title="We Would Love to Meet You"
          subtitle="Request a consultation below, or reach us instantly by phone, WhatsApp or email."
          testid="contact-title"
        />

        <div className="mt-14 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="border border-[#1A1A1A]/10 p-8 sm:p-10" data-testid="contact-form-card">
              <h2 className="font-display text-3xl font-normal text-[#1A1A1A]">Request a Consultation</h2>
              <p className="mt-2 text-sm font-light text-[#5A504B]">Our concierge team responds within 24 hours — usually much sooner.</p>
              <div className="mt-7">
                <EnquiryForm prefix="contact-enquiry" />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            {rows.map((c, i) => {
              const inner = (
                <div className="flex items-start gap-4 border-b border-[#1A1A1A]/10 py-5" data-testid={c.testid}>
                  <c.icon className="mt-1 h-4 w-4 shrink-0 text-[#B38B2D]" aria-hidden="true" />
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-[#1A1A1A]/45">{c.label}</p>
                    <p className="mt-1.5 text-sm font-medium text-[#1A1A1A]">{c.value}</p>
                  </div>
                </div>
              );
              return (
                <Reveal key={c.label} delay={i * 0.07}>
                  {c.href ? (
                    <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block transition-colors hover:bg-[#F5EBE6]/50">
                      {inner}
                    </a>
                  ) : (
                    inner
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* FAQ */}
        <section className="mt-24 max-w-4xl" data-testid="faq-section">
          <Reveal><p className="eyebrow">Questions, Answered</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-display text-4xl font-normal text-[#1A1A1A] sm:text-5xl">Before You Ask</h2>
          </Reveal>
          <div className="mt-10">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <div className="border-b border-[#1A1A1A]/10">
                  <button
                    onClick={() => setOpen(open === i ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={open === i}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className="font-display text-xl font-normal text-[#1A1A1A] sm:text-2xl">{f.q}</span>
                    <motion.span animate={{ rotate: open === i ? 45 : 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="shrink-0 text-[#B38B2D]">
                      <Plus className="h-5 w-5" aria-hidden="true" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="max-w-2xl pb-6 text-sm font-light leading-relaxed text-[#5A504B]">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {s.map_embed_url && (s.section_visibility?.contact_map ?? true) && (
          <Reveal className="mt-20">
            <div className="overflow-hidden rounded-[4px] border border-[#1A1A1A]/10" data-testid="contact-map-card">
              <iframe
                src={s.map_embed_url}
                title="Royal Skin Cosmetic Clinic location map"
                loading="lazy"
                data-testid="contact-map"
                className="h-80 w-full border-0"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
